import { createSlice } from "@reduxjs/toolkit";

import { wizardCreateService } from "./wizardCreateService";
import { changeEmailsData } from "../../utils";

const initialState = {
  migration: null,
  firstFromTo: "",
  firstRoute: "",
  fromTo: {
    source: "",
    target: "",
    preview: "",
  },
  fromToParam: {
    prefix: "",
    route: "",
  },

  emptyMapping: false,
  emails: [],
  notificationData: null,
  messageData: null,
  globalNotification: "",
  accountSelectionStep: {
    emails: [],
    loading: false,
  },
  notice: {},
  demoVideo: null,
  additionalOptionsStep: {
    allOptions: null,
    allOptionsData: [],
  },
  stores: {
    source: [],
    target: [],
    preview: [],
  },
  fields: {
    source: {},
    target: {},
  },
  storesConnect: {
    source: "",
    target: "",
  },
  storePreview: false,
  storePossibleUrls: {
    source: [],
    target: [],
  },
  isPreviewEnabled: false,
  skipConnection: {
    sourceSkipConnection: false,
    targetSkipConnection: false,
  },
  bridgeConnect: {
    source: false,
    target: false,
  },
  tablesPrefix: {
    source: "",
    target: "",
  },
  demoProgress: {
    entities: [],
    show_survey: false,
  },

  disabledEntities: {
    recent: true,
    smartUpdate: true,
  },
  wizardLoading: true,
};

export const wizardCreateSlice = createSlice({
  name: "wizard",
  initialState,

  reducers: {
    deleteMigrationId: (state) => {
      state.migration.id = "";
    },

    setEmptyMapping: (state, action) => {
      state.emptyMapping = action.payload;
    },

    setNotification: (state, action) => {
      state.notificationData = action.payload;
    },

    setMessage: (state, action) => {
      state.messageData = action.payload;
    },

    setGlobalNotification: (state, action) => {
      state.globalNotification = action.payload;
    },

    setFromTo: (state, action) => {
      state.fromTo = action.payload;
    },

    setFirstFromTo: (state, action) => {
      state.firstFromTo = action.payload;
    },

    setFirstRoute: (state, action) => {
      state.firstRoute = action.payload;
    },

    setFromToParam: (state, action) => {
      state.fromToParam = action.payload;
    },

    setFromToParamRoute: (state, action) => {
      state.fromToParam.route = action.payload;
    },

    setNewTargetStore: (state, action) => {
      state.migration.target_store.cart = action.payload;
    },

    setNewSourceStore: (state, action) => {
      state.migration.source_store.cart = action.payload;
    },

    setBridgeConnect: (state, action) => {
      state.bridgeConnect[action.payload.type] = action.payload.connect;
    },

    setStoreConnect: (state, action) => {
      state.storesConnect[action.payload.type] = action.payload.connect;
    },

    setDefaultStatus: (state) => {
      state.migration.status = "created";
    },

    setTargetUrlReset: (state) => {
      state.migration.target_store.url = "";
    },

    setDemoVideo: (state, { payload }) => {
      state.demoVideo = payload;
    },

    setSourceSkipConnection: (state, action) => {
      state.skipConnection.sourceSkipConnection = action.payload;
    },

    setTargetSkipConnection: (state, action) => {
      state.skipConnection.targetSkipConnection = action.payload;
    },

    setSourceTablesPrefix: (state, action) => {
      state.tablesPrefix.source = action.payload;
    },

    setTargetTablesPrefix: (state, action) => {
      state.tablesPrefix.target = action.payload;
    },

    setSmartUpdateDisabled: (state, action) => {
      state.disabledEntities.smartUpdate = action.payload;
    },

    setResentDisabled: (state, action) => {
      state.disabledEntities.recent = action.payload;
    },

    setWizardLoading: (state, action) => {
      state.wizardLoading = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      wizardCreateService.endpoints.getMigrationSession.matchFulfilled,
      (state, { payload }) => {
        state.migration = payload?.payload;
        state.demoProgress.entities = (
          payload?.payload?.entities?.filter((entity) => entity.migrate) || []
        )?.map((item) => ({
          text: item.type,
          percent: 100,
        }));
      },
    );
    builder.addMatcher(
      wizardCreateService.endpoints.getNewMigrationSession.matchFulfilled,
      (state, { payload }) => {
        state.migration = payload?.payload;
      },
    );
    builder.addMatcher(
      wizardCreateService.endpoints.getMigrationSession.matchRejected,
      (state) => {
        state.migration = null;
      },
    );
    builder.addMatcher(
      wizardCreateService.endpoints.getNewMigrationSession.matchRejected,
      (state) => {
        state.migration = null;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.createMigration.matchFulfilled,
      (state, { payload }) => {
        state.migration = null;
        state.migration = payload?.payload;
        state.additionalOptionsStep.allOptions = null;
        state.additionalOptionsStep.allOptionsData = [];
        state.demoProgress.entities = [];
        state.fields.source = {};
        state.fields.target = {};
        state.storesConnect.source = "";
        state.storesConnect.target = "";
        state.bridgeConnect.source = false;
        state.bridgeConnect.target = false;
        state.tablesPrefix.source = "";
        state.tablesPrefix.target = "";
        state.storePossibleUrls.source = [];
        state.storePossibleUrls.target = [];
        state.storePreview = false;
        state.disabledEntities.recent = true;
        state.disabledEntities.smartUpdate = true;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.createMigration.matchRejected,
      (state) => {
        state.migration = null;
        state.additionalOptionsStep.allOptions = null;
        state.additionalOptionsStep.allOptionsData = [];
        state.demoProgress.entities = [];
        state.fields.source = {};
        state.fields.target = {};
        state.storesConnect.source = "";
        state.storesConnect.target = "";
        state.bridgeConnect.source = false;
        state.bridgeConnect.target = false;
        state.tablesPrefix.source = "";
        state.tablesPrefix.target = "";
        state.storePossibleUrls.source = [];
        state.storePossibleUrls.target = [];
        state.storePreview = false;
        state.disabledEntities.recent = true;
        state.disabledEntities.smartUpdate = true;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.continueMigrationSession.matchFulfilled,
      (state, { payload }) => {
        state.migration = null;
        state.migration = payload?.payload;
        state.additionalOptionsStep.allOptions = null;
        state.additionalOptionsStep.allOptionsData = [];
        state.demoProgress.entities = [];
        state.fields.source = {};
        state.fields.target = {};
        state.storesConnect.source = "";
        state.storesConnect.target = "";
        state.bridgeConnect.source = false;
        state.bridgeConnect.target = false;
        state.tablesPrefix.source = "";
        state.tablesPrefix.target = "";
        state.storePossibleUrls.source = [];
        state.storePossibleUrls.target = [];
        state.storePreview = false;
        state.disabledEntities.recent = true;
        state.disabledEntities.smartUpdate = true;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.editMigrationSession.matchFulfilled,
      (state, { payload }) => {
        state.migration = null;
        state.migration = payload?.payload;
        state.additionalOptionsStep.allOptions = null;
        state.additionalOptionsStep.allOptionsData = [];
        state.demoProgress.entities = [];
        state.fields.source = {};
        state.fields.target = {};
        state.storesConnect.source = "";
        state.storesConnect.target = "";
        state.bridgeConnect.source = false;
        state.bridgeConnect.target = false;
        state.tablesPrefix.source = "";
        state.tablesPrefix.target = "";
        state.storePossibleUrls.source = [];
        state.storePossibleUrls.target = [];
        state.storePreview = false;
        state.disabledEntities.recent = true;
        state.disabledEntities.smartUpdate = true;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getOptions.matchFulfilled,
      (state, payload) => {
        if (
          Object.keys(payload?.payload?.payload?.["data-notices"] || {})?.length
        ) {
          state.additionalOptionsStep.allOptions =
            payload?.payload?.payload?.["data-notices"];
        } else {
          state.additionalOptionsStep.allOptions = null;
        }

        if (payload?.payload?.payload) {
          state.additionalOptionsStep.allOptionsData = payload.payload.payload;
        } else {
          state.additionalOptionsStep.allOptionsData = [];
        }
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.setOptions.matchFulfilled,
      (state, payload) => {
        if (
          Object.keys(payload?.payload?.payload?.["data-notices"] || {})?.length
        ) {
          state.additionalOptionsStep.allOptions =
            payload?.payload?.payload?.["data-notices"];
        } else {
          state.additionalOptionsStep.allOptions = [];
        }

        if (payload?.payload?.payload) {
          state.additionalOptionsStep.allOptionsData = payload.payload.payload;
        } else {
          state.additionalOptionsStep.allOptionsData = [];
        }
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getEmails.matchFulfilled,
      (state, { payload }) => {
        if (payload?.payload?.length) {
          state.accountSelectionStep.emails = changeEmailsData(
            payload?.payload,
          );
        }

        state.accountSelectionStep.loading = false;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getEmails.matchPending,
      (state) => {
        state.accountSelectionStep.loading = true;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getEmails.matchRejected,
      (state) => {
        state.accountSelectionStep.loading = false;
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getAvailableStores.matchFulfilled,
      (state, { payload }) => {
        state.isPreviewEnabled = !!payload?.payload?.carts_preview_enabled;
        if (Object.values(payload?.payload?.source_carts || {})?.length) {
          state.stores.source = Object.values(
            payload?.payload?.source_carts || {},
          )
            .sort((a, b) => b.is_supported - a.is_supported)
            .map((store) => ({
              value: store?.id,
              label: store?.title,
              is_supported: store?.is_supported,
              cart_url: store?.cart_url,
            }));
        }

        if (Object.values(payload?.payload?.target_carts || {})?.length) {
          state.stores.target = Object.values(
            payload?.payload?.target_carts || {},
          )
            .sort((a, b) => b.is_supported - a.is_supported)
            .map((store) => ({
              value: store?.id,
              label: store?.title,
              is_supported: store?.is_supported,
              cart_url: store?.cart_url,
            }));
        }

        if (
          Object.values(payload?.payload?.carts_preview_carts || {})?.length
        ) {
          state.stores.preview = Object.values(
            payload?.payload?.carts_preview_carts || {},
          ).map((store) => ({
            value: store?.id,
            label: store?.name,
            cart_url: store?.cart_url,
          }));
        }
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.checkTypeConnect.matchFulfilled,
      (state, { payload }) => {
        state.fields.source = payload?.payload?.fields?.source;
        state.fields.target = payload?.payload?.fields?.target;

        if (payload?.payload?.storetype === "source") {
          state.storePossibleUrls.source = payload?.payload?.possible_urls;

          if (!state.storesConnect.source) {
            state.storesConnect.source =
              payload?.payload?.fields?.source?.sets?.default ||
              payload?.payload?.cartMigrationType;
          }
        }

        if (payload?.payload?.storetype === "target") {
          state.storePreview = payload?.payload?.isCartPreview;
          state.storePossibleUrls.target = payload?.payload?.possible_urls;
          const sets = payload?.payload?.fields?.target?.sets;

          if (
            !state.storesConnect.target ||
            (!sets && payload?.payload?.isCartPreview)
          ) {
            state.storesConnect.target =
              sets?.default || payload?.payload?.cartMigrationType;
          }
        }
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.setupStores.matchFulfilled,
      (state) => {
        state.storesConnect.source = "";
        state.storesConnect.target = "";
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getDemoProgressEntities.matchFulfilled,
      (state, { payload }) => {
        state.demoProgress.entities = Object.keys(
          payload?.payload?.entities || {},
        )?.map((item) => ({
          name: item,
          percent: 100,
        }));
      },
    );

    builder.addMatcher(
      wizardCreateService.endpoints.getNoticesByStep.matchFulfilled,
      (state, { payload }) => {
        state.notice = payload?.payload;
      },
    );
  },
});

export const {
  deleteMigrationId,
  setBridgeConnect,
  setStoreConnect,
  setNotification,
  setMessage,
  setNewTargetStore,
  setNewSourceStore,
  setFromTo,
  setDefaultStatus,
  setEmptyMapping,
  setFromToParam,
  setFirstFromTo,
  setFirstRoute,
  setFromToParamRoute,
  setTargetUrlReset,
  setGlobalNotification,
  setSourceSkipConnection,
  setTargetSkipConnection,
  setSourceTablesPrefix,
  setTargetTablesPrefix,
} = wizardCreateSlice.actions;

export const selectMigrationSession = (state) => state.wizard.migration;
export const selectBridgeConnect = (state) => state.wizard.bridgeConnect;
export const selectCartFields = (state) => state.wizard.fields;
export const selectNotice = (state) => state.wizard.notice;
export const selectDemoVideo = (state) => state.wizard.demoVideo;
export const selectStores = (state) => state.wizard.stores;
export const selectStoresConnect = (state) => state.wizard.storesConnect;
export const selectAccountSelectionStep = (state) =>
  state.wizard.accountSelectionStep;
export const selectAdditionalOptionsStep = (state) =>
  state.wizard.additionalOptionsStep;
export const selectDemoProgress = (state) => state.wizard.demoProgress;
export const selectNotification = (state) => state.wizard.notificationData;
export const selectMessage = (state) => state.wizard.messageData;
export const selectFromTo = (state) => state.wizard.fromTo;
export const selectEmptyMapping = (state) => state.wizard.emptyMapping;
export const selectFromToParam = (state) => state.wizard.fromToParam;
export const selectGlobalNotification = (state) =>
  state.wizard.globalNotification;

export const selectConnectionSkip = (state) => state.wizard.skipConnection;
export const selectTablesPrefix = (state) => state.wizard.tablesPrefix;
export const selectStorePreview = (state) => state.wizard.storePreview;
export const selectStorePossibleUrls = (state) =>
  state.wizard.storePossibleUrls;

export const selectFirstFromTo = (state) => state.wizard.firstFromTo;
export const selectFirstRoute = (state) => state.wizard.firstRoute;
export const selectPreviewEnabled = (state) => state.wizard.isPreviewEnabled;
export const selectDisabledEntities = (state) => state.wizard.disabledEntities;
export const selectWizardLoading = (state) => state.wizard.wizardLoading;

export const {
  setDemoVideo,
  setSmartUpdateDisabled,
  setResentDisabled,
  setWizardLoading,
} = wizardCreateSlice.actions;

export default wizardCreateSlice.reducer;
